// =============================================================================
// app/src/lib/lerBoletoArquivo.ts — do arquivo escolhido aos códigos lidos.
//
// A parte que fala com o navegador (o leitor em si, puro e testado, está em
// lerBoleto.ts). Recebe a foto ou o PDF do boleto e devolve as linhas
// digitáveis — nunca adivinhadas: cada uma passou pelos dígitos
// verificadores, e a das barras foi lida igual por pelo menos duas linhas da
// imagem.
//
// Três caminhos, do mais barato ao mais caro (28/10/2026):
//   1. PDF com TEXTO (o que o banco manda por e-mail): a linha digitável já
//      está escrita nele — lerDasArquivo.textoDoPdf + linhasNoTexto;
//   2. as BARRAS, nas fotos (digitalização) — lerBoleto.ts;
//   3. os NÚMEROS impressos em cima das barras, quando elas não dão — a
//      impressora que espalha tinta come os espaços finos do código, e os
//      números continuam legíveis (lerLinhaImpressa.ts, carregado só aqui:
//      os modelos dos dígitos pesam ~28 KB e o balcão não precisa deles).
//
// Lê o arquivo ORIGINAL. O anexo que a conta guarda passa pelo compressor da
// foto de peça e sai com 1.280 px — nas fotos da loja medidas em 18/09/2026,
// nenhuma das reduzidas deu para ler; as digitalizações inteiras, sim.
// =============================================================================
import { textoDoPdf } from './lerDasArquivo';
import { fotosDoPdf, lerCodigosDeBarras, luminanciaDe, type Leitura } from './lerBoleto';

export type ResultadoDaLeitura =
  | { ok: true; leituras: Leitura[] }
  | { ok: false; motivo: 'sem-codigo' | 'pdf-sem-foto' | 'pdf-sem-codigo' | 'formato' };

/** As digitalizações do iPhone têm até 3.264 px; a foto da câmera, 4.032.
 *  Acima disto o canvas do iPhone pesa e as barras não ganham nada. */
const LADO_MAXIMO = 3400;

async function luminanciaDoArquivo(blob: Blob) {
  const bmp = await createImageBitmap(blob).catch(() => null);
  if (!bmp) return null;
  const escala = Math.min(1, LADO_MAXIMO / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * escala));
  const h = Math.max(1, Math.round(bmp.height * escala));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) { bmp.close(); return null; }
  ctx.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  const { data } = ctx.getImageData(0, 0, w, h);
  canvas.width = 0; // devolve a memória do canvas já (o Safari segura)
  return luminanciaDe(data, w, h);
}

export async function lerBoletoDoArquivo(arquivo: File): Promise<ResultadoDaLeitura> {
  let imagens: Blob[];
  const ehPdf = arquivo.type === 'application/pdf' || /\.pdf$/i.test(arquivo.name);
  if (ehPdf) {
    const bytes = new Uint8Array(await arquivo.arrayBuffer());
    // O PDF do banco: a linha está escrita, não precisa ler imagem nenhuma.
    const { linhasNoTexto } = await import('./lerLinhaImpressa');
    const escritas = linhasNoTexto(await textoDoPdf(bytes));
    if (escritas.length) return { ok: true, leituras: escritas };
    // Até 6 fotos: a digitalização de duas folhas tem 2, e cada página pode
    // trazer uma miniatura junto (240 px), que não tem barra que se leia.
    const fotos = fotosDoPdf(bytes).slice(0, 6);
    if (!fotos.length) return { ok: false, motivo: 'pdf-sem-foto' };
    imagens = fotos.map((f) => new Blob([f.slice()], { type: 'image/jpeg' }));
  } else if (arquivo.type.startsWith('image/')) {
    imagens = [arquivo];
  } else {
    return { ok: false, motivo: 'formato' };
  }

  const achadas = new Map<string, Leitura>();
  let temFolha = false;
  // As folhas em que as barras não deram nada — no PDF de 5 páginas do dia
  // 28/10/2026, 3 liam pelas barras e 2 (a impressora da FW) não.
  const semBarras: Blob[] = [];
  for (const img of imagens) {
    // Deixa a tela pintar o "lendo…" antes do trabalho pesado.
    await new Promise((r) => setTimeout(r, 0));
    const lum = await luminanciaDoArquivo(img);
    if (!lum) continue;
    // Foto do tamanho de uma folha digitalizada (o logotipo do banco no PDF
    // gerado tem poucas centenas de pixels).
    if (Math.max(lum.largura, lum.altura) >= 1000) temFolha = true;
    const lidas = lerCodigosDeBarras(lum);
    if (!lidas.length && Math.max(lum.largura, lum.altura) >= 1000) semBarras.push(img);
    for (const l of lidas) {
      const antes = achadas.get(l.linha);
      if (!antes || antes.votos < l.votos) achadas.set(l.linha, l);
    }
  }
  // Onde as barras não deram: os números impressos. A imagem é lida de novo
  // em vez de guardada — seis folhas de 3.400 px seriam ~70 MB presos no
  // iPhone.
  if (semBarras.length) {
    const { lerLinhasImpressas } = await import('./lerLinhaImpressa');
    for (const img of semBarras) {
      await new Promise((r) => setTimeout(r, 0));
      const lum = await luminanciaDoArquivo(img);
      if (!lum) continue;
      for (const l of lerLinhasImpressas(lum)) if (!achadas.has(l.linha)) achadas.set(l.linha, l);
    }
  }
  const leituras = [...achadas.values()].sort((a, b) => b.votos - a.votos);
  if (leituras.length) return { ok: true, leituras };
  if (ehPdf) return { ok: false, motivo: temFolha ? 'pdf-sem-codigo' : 'pdf-sem-foto' };
  return { ok: false, motivo: 'sem-codigo' };
}
